import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, CircularProgress, Typography, Chip, TablePagination } from '@mui/material';
import ViewIcon from '@mui/icons-material/Visibility';
import Table from '../../components/Table/Table';
import { useNavigate } from 'react-router-dom';
import { getOrders, getMcbOrderStatus, updateOrderStatus } from '../../api/api';

/**
 * Props:
 *   filters?: { search?: string, status?: string, priority?: string, payment_status?: string }
 *   onView?: (order) => void   // not used when navigating
 */
const OrderManagementTable = ({ filters = {} }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [paymentStatuses, setPaymentStatuses] = useState({}); // Map orderId -> payment status
  const [page, setPage] = useState(0); // 0-indexed for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1)); // API uses 1-indexed, TablePagination uses 0-indexed
    params.set('per_page', String(rowsPerPage));
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search.trim());
    if (filters.priority) params.set('priority', filters.priority);
    return params.toString();
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await getOrders(qs); // admin sees all orders (server enforces)
        if (!alive) return;
        const ordersList = Array.isArray(res?.orders) ? res.orders : [];
        setOrders(ordersList);
        
        // Set total count from API response
        setTotalCount(res?.total || res?.total_count || ordersList.length);
        
        // Optimized payment status fetching:
        // - Only call MCB API for "AWAITING PAYMENT" status
        // - For PAID and after: show "Paid" without API call
        // - For before AWAITING PAYMENT and CANCELLED: show "-" without API call
        const statusPromises = ordersList.map(async (order) => {
          const orderStatus = String(order.status || '').trim().toUpperCase();
          
          console.log(`[Admin Portal] Processing order ${order.id} (${order.order_number}), status: "${order.status}" -> normalized: "${orderStatus}"`);
          
          // Statuses before AWAITING PAYMENT or CANCELLED: show "-"
          const beforeAwaitingPayment = [
            'AWAITING VENDOR CONFIRMATION',
            'VENDOR VALIDATION REQUESTED',
            'VENDOR CONFIRMED'
          ];
          
          if (beforeAwaitingPayment.includes(orderStatus) || orderStatus === 'CANCELLED') {
            console.log(`[Admin Portal] Order ${order.id}: Status is before AWAITING PAYMENT or CANCELLED, showing "-"`);
            return { orderId: order.id, status: 'NOT_APPLICABLE' }; // Special marker for "-"
          }
          
          // PAID and all statuses after PAID: show "Paid" without API call
          const paidAndAfter = [
            'PAID',
            'VENDOR ORDER PROCESSED',
            'ASSEMBLING ORDER',
            'SHIPPED',
            'DELIVERED'
          ];
          
          if (paidAndAfter.includes(orderStatus)) {
            console.log(`[Admin Portal] Order ${order.id}: Status is PAID or after, showing "Paid" without API call`);
            return { orderId: order.id, status: 'PAID' }; // Directly mark as paid
          }
          
          // If order status is AWAITING PAYMENT -> call MCB API
          if (orderStatus === 'AWAITING PAYMENT') {
            try {
              console.log(`[Admin Portal] Order ${order.id} (${order.order_number}) is AWAITING PAYMENT -> Calling MCB API`);
              const response = await getMcbOrderStatus(order.id);
              if (!alive) return { orderId: order.id, status: 'GENERATED' };
              
              console.log(`[Admin Portal] MCB API response for order ${order.id}:`, response);
              
              if (!response) {
                console.log(`[Admin Portal] MCB API returned null for order ${order.id}`);
                return { orderId: order.id, status: 'GENERATED' };
              }
              
              // STRICT LOGIC (aligned with backend mapper):
              // - Only mark as PAID if ALL THREE are success/approved:
              //     * gateway_status in {CAPTURED, CAPTURED_PARTIALLY, PAID, COMPLETED}
              //     * result == 'SUCCESS'
              //     * payment_status == 'PAID'
              // - If payment_status is FAILED or gateway_status is FAILED -> FAILED
              // - Everything else -> GENERATED (Awaiting Payment)
              const result = String(response.result || '').toUpperCase();
              const gatewayStatus = String(response.gateway_status || '').toUpperCase();
              const paymentStatus = String(response.payment_status || '').toUpperCase();

              console.log(
                `[Admin Portal] MCB result for order ${order.id}: `
                + `"${result}", gateway_status: "${gatewayStatus}", payment_status: "${paymentStatus}"`
              );

              const successStatuses = ['CAPTURED', 'CAPTURED_PARTIALLY', 'PAID', 'COMPLETED'];
              const allSuccess =
                successStatuses.includes(gatewayStatus) &&
                result === 'SUCCESS' &&
                paymentStatus === 'PAID';

              const hasFailedSignal =
                gatewayStatus === 'FAILED' || paymentStatus === 'FAILED';

              if (allSuccess) {
                console.log(`[Admin Portal] ✅ Triple-check success -> Order ${order.id} is PAID`);
                // Backend already updates order status, but refresh local state
                return { orderId: order.id, status: 'PAID' };
              }

              if (hasFailedSignal) {
                console.log(`[Admin Portal] ❌ MCB indicates FAILED -> Order ${order.id} marked as FAILED`);
                return { orderId: order.id, status: 'FAILED' };
              }

              console.log(`[Admin Portal] MCB result not conclusive -> Order ${order.id} still Awaiting Payment`);
              return { orderId: order.id, status: 'GENERATED' };
            } catch (error) {
              console.error(`[Admin Portal] Error calling MCB API for order ${order.id}:`, error);
              return { orderId: order.id, status: 'GENERATED' };
            }
          }
          
          // Fallback for unknown statuses
          return { orderId: order.id, status: null };
        });
        
        const statusResults = await Promise.all(statusPromises);
        if (!alive) return;
        
        const statusMap = {};
        statusResults.forEach(({ orderId, status }) => {
          statusMap[orderId] = status;
        });
        setPaymentStatuses(statusMap);
      } catch (e) {
        if (!alive) return;
        console.error('Failed to load orders', e);
        setErr(e?.message || 'Failed to load orders');
        setOrders([]);
        setTotalCount(0);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [qs]);
  
  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters.status, filters.search, filters.priority, filters.payment_status]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const columns = [
    { id: 'order_number', label: 'Order#' },
    { id: 'customer', label: 'Customer' },
    { id: 'status', label: 'Status' },
    { id: 'priority', label: 'Priority' },
    { id: 'quantity', label: 'QTY' },
    { id: 'date', label: 'Date' },
    { id: 'total', label: 'Total' },
    { id: 'payment_status', label: 'Payment Status' },
    { id: 'action', label: 'Action' },
  ];
  
  // Map API payment_status to display status - Only 2 statuses: "Paid" or "Awaiting Payment"
  const mapApiStatusToDisplay = (apiStatus, orderStatus) => {
    // Special case: NOT_APPLICABLE means show "-"
    if (apiStatus === 'NOT_APPLICABLE') {
      return null; // Will be displayed as "-"
    }
    
    // If order is already PAID or after PAID, show "Paid"
    const orderStatusUpper = orderStatus?.toUpperCase();
    const paidAndAfter = ['PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];
    if (paidAndAfter.includes(orderStatusUpper)) {
      return "Paid";
    }
    
    // For AWAITING PAYMENT status, use MCB API response
    // Primary statuses:
    // - PAID -> "Paid"
    // - FAILED -> "Failed"
    // - Everything else -> "Awaiting Payment"
    if (!apiStatus) return "Awaiting Payment";
    const upper = apiStatus.toUpperCase();
    if (upper === "PAID") return "Paid";
    if (upper === "FAILED") return "Failed";
    // Everything else (GENERATED, PENDING, etc.) -> "Awaiting Payment"
    return "Awaiting Payment";
  };
  
  // Payment status colors - same as PaymentConfirmation
  const PAYMENT_STATUS_COLORS = {
    "Awaiting Payment": { border: "#ffeb3b", text: "#f57f17" }, // Yellow
    "Paid": { border: "#4caf50", text: "#1b5e20" }, // Green
    "Failed": { border: "#f44336", text: "#b71c1c" }, // Red
  };

  const currency = (n) => `$${Number(n || 0).toFixed(2)}`;
  
  // Filter orders by payment status (client-side filtering)
  const filteredOrders = useMemo(() => {
    if (!filters.payment_status) return orders;
    
    return orders.filter((o) => {
      const apiStatus = paymentStatuses[o.id];
      const displayStatus = mapApiStatusToDisplay(apiStatus, o.status);
      // If displayStatus is null (shows "-"), skip filtering by payment_status
      if (displayStatus === null) return filters.payment_status === '—';
      return displayStatus === filters.payment_status;
    });
  }, [orders, paymentStatuses, filters.payment_status]);
  
  const rows = filteredOrders.map((o) => {
    const qty = Array.isArray(o.items) ? o.items.reduce((a, it) => a + Number(it.quantity || 0), 0) : 0;
    const apiStatus = paymentStatuses[o.id];
    const displayStatus = mapApiStatusToDisplay(apiStatus, o.status);
    
    // If displayStatus is null, show "-" (for not applicable cases)
    const paymentStatusDisplay = displayStatus === null ? '—' : displayStatus;
    const statusColor = displayStatus ? (PAYMENT_STATUS_COLORS[displayStatus] || PAYMENT_STATUS_COLORS["Awaiting Payment"]) : { border: "#cccccc", text: "#666666" };
    
    // Check if order has any ON_DEMAND items
    // Item is ON_DEMAND if pricing_mode is ON_DEMAND AND unit_price is 0/null/undefined
    // If unit_price > 0, it means price is confirmed, so include in calculations
    const hasOnDemandItems = Array.isArray(o.items) && o.items.some(item => {
      const unitPriceNum = Number(item.unit_price ?? item.product_price ?? 0);
      return (item.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
             (item.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
    });
    
    return {
      order_number: o.order_number,
      customer: o.customer_name || '—',
      status: o.status,
      priority: o.priority || 'STANDARD',
      quantity: qty,
      date: (o.created_at || '').slice(0, 10),
      total: hasOnDemandItems ? (
        <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>
      ) : currency(o.total_amount),
      payment_status: (
        <Chip
          label={paymentStatusDisplay}
          size="small"
          sx={{
            backgroundColor: '#ffffff',
            color: statusColor.text,
            borderColor: statusColor.border,
            borderWidth: 2,
            borderStyle: 'solid',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: '24px',
          }}
        />
      ),
      action: (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={() => navigate(`/orders/${o.id}`, { state: { order: o } })}
            title={`View ${o.order_number}`}
          >
            <ViewIcon />
          </IconButton>
        </Box>
      ),
    };
  });

  return (
    <Box padding={3}>
      {loading ? (
        <Box display="flex" alignItems="center" gap={1}><CircularProgress size={18}/><Typography>Loading…</Typography></Box>
      ) : err ? (
        <Typography color="error">{err}</Typography>
      ) : (
        <Box>
          <Table columns={columns} rows={rows} title="Order Management" />
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
          />
        </Box>
      )}
    </Box>
  );
};

export default OrderManagementTable;
